// The host side of a framed presentation (see ../presentationFrame.ts).
//
// The hook listens for the framed page's messages, answers its `ready` with
// whether Ask is open here, and hands an `ask` or a `location` to the host.
// It sends nothing to the agent: an ask opens the host's dialog, no more.
//
// With no `askState` the hook stays silent, so the page draws no Ask button
// (a surface that offers no follow-up). Pages published before decks never
// send `ready`, so they never hear from the host either.

import { useEffect, useRef, useState, type RefObject } from 'react'
import {
  askStateMessage,
  goMessage,
  postToDeck,
  readDeckMessage,
  type DeckAsk,
  type DeckLocation,
  type DeckSlide,
  type PresentationAskState,
} from '../presentationFrame.js'

export interface UsePresentationFrameOptions {
  frameRef: RefObject<HTMLIFrameElement | null>
  /** Whether the person may ask here, and why not. Omitted: the host says nothing and the page draws no Ask. */
  askState?: PresentationAskState
  /** The person pressed Ask on the page. Opens a dialog; never sends. */
  onAsk?: (ask: DeckAsk) => void
  /** The page moved to another slide or layer. */
  onLocation?: (at: DeckLocation) => void
}

export interface PresentationFrame {
  /** The deck the page announced, or null for a page that never said `ready`. */
  deck: { title: string; slides: DeckSlide[] } | null
  /** Moves the deck to a slide or layer. Does nothing before `ready`. */
  handleGo: (at: DeckLocation) => void
}

export default function usePresentationFrame({ frameRef, askState, onAsk, onLocation }: UsePresentationFrameOptions): PresentationFrame {
  const [deck, setDeck] = useState<PresentationFrame['deck']>(null)
  // Read by the listener, which is attached once.
  const latest = useRef({ askState, onAsk, onLocation })
  latest.current = { askState, onAsk, onLocation }
  const readyRef = useRef(false)

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const msg = readDeckMessage(event, frameRef.current?.contentWindow)
      if (!msg) return
      const { askState: state, onAsk: ask, onLocation: moved } = latest.current
      if (msg.type === 'ready') {
        readyRef.current = true
        setDeck({ title: msg.title, slides: msg.slides })
        if (state) postToDeck(frameRef.current?.contentWindow, askStateMessage(state))
        return
      }
      if (msg.type === 'location') {
        moved?.(msg.at)
        return
      }
      // An ask only counts where the host opened Ask; a page that draws its
      // button anyway is ignored.
      if (msg.type === 'ask' && state?.enabled && ask) ask(msg.ask)
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // Ask opened or closed after the page said `ready` (AI access answered
  // late, say): tell the page again.
  const enabled = askState?.enabled
  const reason = askState?.reason
  useEffect(() => {
    if (!readyRef.current || enabled === undefined) return
    postToDeck(frameRef.current?.contentWindow, askStateMessage({ enabled, reason }))
  }, [enabled, reason])

  return {
    deck,
    handleGo: (at) => {
      if (!readyRef.current) return
      postToDeck(frameRef.current?.contentWindow, goMessage(at))
    },
  }
}
